import React, { useState, useEffect, useCallback } from 'react';
import {
  Mail, Inbox, Send, Trash2, Archive, Star, Search, Plus, RefreshCw,
  X, Loader2, ChevronLeft, Paperclip, Reply, Forward, Eye, EyeOff,
  FolderOpen, Settings, AlertCircle
} from 'lucide-react';
import api, { EmailAccount, EmailFolder, EmailMessageSummary, EmailMessageDetail } from '../services/api';

const FOLDER_ICONS: Record<string, React.FC<{ className?: string }>> = {
  'INBOX': Inbox,
  'INBOX.Sent': Send,
  'INBOX.Trash': Trash2,
  'INBOX.Archive': Archive,
  'INBOX.Drafts': Mail,
  'INBOX.Junk': AlertCircle,
  'INBOX.spam': AlertCircle,
};

const FOLDER_LABELS: Record<string, string> = {
  'INBOX': 'Inbox',
  'INBOX.Sent': 'Sent',
  'INBOX.Trash': 'Trash',
  'INBOX.Archive': 'Archive',
  'INBOX.Drafts': 'Drafts',
  'INBOX.Junk': 'Junk',
  'INBOX.spam': 'Spam',
};

const EmailPage: React.FC = () => {
  // Accounts
  const [accounts, setAccounts] = useState<EmailAccount[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<EmailAccount | null>(null);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [accountForm, setAccountForm] = useState({
    email_address: '', password: '', display_name: '',
    imap_host: 'imap.migadu.com', imap_port: 993,
    smtp_host: 'smtp.migadu.com', smtp_port: 465,
  });
  const [accountError, setAccountError] = useState('');
  const [isAddingAccount, setIsAddingAccount] = useState(false);

  // Folders
  const [folders, setFolders] = useState<EmailFolder[]>([]);
  const [selectedFolder, setSelectedFolder] = useState('INBOX');

  // Messages
  const [messages, setMessages] = useState<EmailMessageSummary[]>([]);
  const [totalMessages, setTotalMessages] = useState(0);
  const [selectedMessage, setSelectedMessage] = useState<EmailMessageDetail | null>(null);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [syncing, setSyncing] = useState(false);

  // Compose
  const [showCompose, setShowCompose] = useState(false);
  const [composeMode, setComposeMode] = useState<'new' | 'reply' | 'forward'>('new');
  const [composeData, setComposeData] = useState({
    to: '', cc: '', bcc: '', subject: '', body: '',
    in_reply_to: '', references: '',
  });
  const [sending, setSending] = useState(false);

  const [error, setError] = useState('');

  // Fetch accounts
  const fetchAccounts = useCallback(async () => {
    try {
      const data = await api.getEmailAccounts();
      const accountList = Array.isArray(data) ? data : (data as any).results || [];
      setAccounts(accountList);
      if (accountList.length > 0 && !selectedAccount) {
        setSelectedAccount(accountList[0]);
      }
    } catch (err) {
      setError('Failed to load email accounts');
    }
  }, [selectedAccount]);

  // Fetch folders
  const fetchFolders = useCallback(async () => {
    if (!selectedAccount) return;
    try {
      const data = await api.getEmailFolders(selectedAccount.id);
      setFolders(data);
    } catch (err) { /* non-critical */ }
  }, [selectedAccount]);

  // Fetch messages
  const fetchMessages = useCallback(async () => {
    if (!selectedAccount) return;
    setLoadingMessages(true);
    try {
      const data = await api.getEmailMessages(selectedAccount.id, {
        folder: selectedFolder,
        search: searchQuery || undefined,
        page_size: 50,
      });
      setMessages(data.results);
      setTotalMessages(data.count);
    } catch (err) {
      setError('Failed to load messages');
    } finally {
      setLoadingMessages(false);
    }
  }, [selectedAccount, selectedFolder, searchQuery]);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);
  useEffect(() => { fetchFolders(); }, [fetchFolders]);
  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  // Sync
  const handleSync = async () => {
    if (!selectedAccount) return;
    setSyncing(true);
    try {
      await api.syncEmailAccount(selectedAccount.id, selectedFolder);
      await fetchMessages();
      await fetchFolders();
    } catch (err) {
      setError('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  // Select message
  const handleSelectMessage = async (msg: EmailMessageSummary) => {
    if (!selectedAccount) return;
    setLoadingMessage(true);
    try {
      const detail = await api.getEmailMessage(selectedAccount.id, msg.id);
      setSelectedMessage(detail);
      // Update local read state
      if (!msg.is_read) {
        setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, is_read: true } : m));
      }
    } catch (err) {
      setError('Failed to load message');
    } finally {
      setLoadingMessage(false);
    }
  };

  // Toggle read
  const handleToggleRead = async (msg: EmailMessageSummary, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!selectedAccount) return;
    try {
      await api.updateEmailMessage(selectedAccount.id, msg.id, { is_read: !msg.is_read });
      setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, is_read: !m.is_read } : m));
    } catch (err) { /* non-critical */ }
  };

  // Delete
  const handleDelete = async (msg: EmailMessageSummary, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!selectedAccount) return;
    try {
      await api.deleteEmailMessage(selectedAccount.id, msg.id);
      setMessages(prev => prev.filter(m => m.id !== msg.id));
      if (selectedMessage?.id === msg.id) setSelectedMessage(null);
    } catch (err) {
      setError('Failed to delete message');
    }
  };

  // Compose
  const openCompose = (mode: 'new' | 'reply' | 'forward' = 'new', replyMsg?: EmailMessageDetail) => {
    setComposeMode(mode);
    if (mode === 'new') {
      setComposeData({ to: '', cc: '', bcc: '', subject: '', body: '', in_reply_to: '', references: '' });
    } else if (mode === 'reply' && replyMsg) {
      const replyTo = replyMsg.from_address;
      const refs = [replyMsg.references, replyMsg.message_id].filter(Boolean).join(' ');
      setComposeData({
        to: replyTo,
        cc: '', bcc: '',
        subject: replyMsg.subject.startsWith('Re:') ? replyMsg.subject : `Re: ${replyMsg.subject}`,
        body: `<br><br><hr><p>On ${new Date(replyMsg.date).toLocaleString()}, ${replyMsg.from_address} wrote:</p><blockquote>${replyMsg.html_body || replyMsg.plain_body || ''}</blockquote>`,
        in_reply_to: replyMsg.message_id,
        references: refs,
      });
    } else if (mode === 'forward' && replyMsg) {
      setComposeData({
        to: '', cc: '', bcc: '',
        subject: replyMsg.subject.startsWith('Fwd:') ? replyMsg.subject : `Fwd: ${replyMsg.subject}`,
        body: `<br><br><hr><p>---------- Forwarded message ----------</p><p>From: ${replyMsg.from_address}<br>Subject: ${replyMsg.subject}<br>Date: ${new Date(replyMsg.date).toLocaleString()}</p><br>${replyMsg.html_body || replyMsg.plain_body || ''}`,
        in_reply_to: '', references: '',
      });
    }
    setShowCompose(true);
  };

  const handleSend = async () => {
    if (!selectedAccount || !composeData.to.trim()) return;
    setSending(true);
    try {
      const toList = composeData.to.split(',').map(s => s.trim()).filter(Boolean);
      const ccList = composeData.cc ? composeData.cc.split(',').map(s => s.trim()).filter(Boolean) : [];
      const bccList = composeData.bcc ? composeData.bcc.split(',').map(s => s.trim()).filter(Boolean) : [];
      await api.sendEmail(selectedAccount.id, {
        to: toList,
        cc: ccList,
        bcc: bccList,
        subject: composeData.subject,
        body: composeData.body || '<p></p>',
        in_reply_to: composeData.in_reply_to,
        references: composeData.references,
      });
      setShowCompose(false);
      setComposeData({ to: '', cc: '', bcc: '', subject: '', body: '', in_reply_to: '', references: '' });
    } catch (err) {
      setError('Failed to send email');
    } finally {
      setSending(false);
    }
  };

  // Add account
  const handleAddAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsAddingAccount(true);
    setAccountError('');
    try {
      await api.createEmailAccount(accountForm);
      setShowAccountModal(false);
      setAccountForm({
        email_address: '', password: '', display_name: '',
        imap_host: 'gtxm1202.siteground.biz', imap_port: 993,
        smtp_host: 'gtxm1202.siteground.biz', smtp_port: 587,
      });
      fetchAccounts();
    } catch (err: any) {
      setAccountError(err?.message || 'Failed to add account. Check credentials.');
    } finally {
      setIsAddingAccount(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    if (isToday) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const getFolderLabel = (name: string) => FOLDER_LABELS[name] || name.replace('INBOX.', '');
  const getFolderIcon = (name: string) => FOLDER_ICONS[name] || FolderOpen;

  // No accounts state
  if (accounts.length === 0) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-black">
        <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
          <div className="max-w-7xl mx-auto px-3 py-2">
            <h1 className="text-base font-semibold text-gray-900 dark:text-white">Email</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">Manage your @digimint.co email</p>
          </div>
        </div>
        <div className="max-w-md mx-auto mt-12 text-center">
          <Mail className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">No email accounts connected</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Add your @digimint.co email to get started.</p>
          <button onClick={() => setShowAccountModal(true)} className="btn btn-primary">
            <Plus className="w-4 h-4" /> Add Email Account
          </button>
          {renderAccountModal()}
        </div>
      </div>
    );
  }

  function renderAccountModal() {
    if (!showAccountModal) return null;
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3">
        <div className="bg-white dark:bg-gray-900 rounded-lg max-w-md w-full border border-light-border dark:border-dark-border">
          <div className="flex items-center justify-between p-3 border-b border-light-border dark:border-dark-border">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Add Email Account</h2>
            <button onClick={() => setShowAccountModal(false)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded">
              <X className="w-4 h-4" />
            </button>
          </div>
          <form onSubmit={handleAddAccount} className="p-3 space-y-2">
            {accountError && (
              <div className="bg-status-error/10 border border-status-error/30 rounded p-2 text-xs text-status-error">{accountError}</div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Email Address *</label>
              <input type="email" required value={accountForm.email_address}
                onChange={(e) => setAccountForm({ ...accountForm, email_address: e.target.value })}
                className="input" placeholder="you@digimint.co" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Password *</label>
              <input type="password" required value={accountForm.password}
                onChange={(e) => setAccountForm({ ...accountForm, password: e.target.value })}
                className="input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Display Name</label>
              <input type="text" value={accountForm.display_name}
                onChange={(e) => setAccountForm({ ...accountForm, display_name: e.target.value })}
                className="input" placeholder="Your Name" />
            </div>
            <details className="text-xs">
              <summary className="cursor-pointer text-gray-500 dark:text-gray-400">Advanced Settings</summary>
              <div className="mt-2 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">IMAP Host</label>
                    <input type="text" value={accountForm.imap_host}
                      onChange={(e) => setAccountForm({ ...accountForm, imap_host: e.target.value })}
                      className="input" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">IMAP Port</label>
                    <input type="number" value={accountForm.imap_port}
                      onChange={(e) => setAccountForm({ ...accountForm, imap_port: parseInt(e.target.value) })}
                      className="input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">SMTP Host</label>
                    <input type="text" value={accountForm.smtp_host}
                      onChange={(e) => setAccountForm({ ...accountForm, smtp_host: e.target.value })}
                      className="input" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">SMTP Port</label>
                    <input type="number" value={accountForm.smtp_port}
                      onChange={(e) => setAccountForm({ ...accountForm, smtp_port: parseInt(e.target.value) })}
                      className="input" />
                  </div>
                </div>
              </div>
            </details>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowAccountModal(false)} className="btn btn-secondary">Cancel</button>
              <button type="submit" disabled={isAddingAccount} className="btn btn-primary">
                {isAddingAccount && <Loader2 className="w-4 h-4 animate-spin" />}
                Connect Account
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-black flex flex-col">
      {/* Header */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
        <div className="px-3 py-2 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-base font-semibold text-gray-900 dark:text-white">Email</h1>
            {accounts.length > 1 && (
              <select
                className="select text-xs"
                value={selectedAccount?.id || ''}
                onChange={(e) => {
                  const acc = accounts.find(a => a.id === e.target.value);
                  if (acc) { setSelectedAccount(acc); setSelectedMessage(null); }
                }}
              >
                {accounts.map(a => <option key={a.id} value={a.id}>{a.email_address}</option>)}
              </select>
            )}
            {accounts.length === 1 && (
              <span className="text-xs text-gray-500 dark:text-gray-400">{selectedAccount?.email_address}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleSync} disabled={syncing} className="btn btn-ghost text-xs">
              <RefreshCw className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`} /> Sync
            </button>
            <button onClick={() => openCompose('new')} className="btn btn-primary text-xs">
              <Plus className="w-3.5 h-3.5" /> Compose
            </button>
            <button onClick={() => setShowAccountModal(true)} className="btn btn-ghost text-xs">
              <Settings className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="px-3 py-1">
          <div className="bg-status-error/10 border border-status-error/30 rounded p-2 text-xs text-status-error flex items-center justify-between">
            {error}
            <button onClick={() => setError('')} className="ml-2"><X className="w-3 h-3" /></button>
          </div>
        </div>
      )}

      {/* Main content - 3 panel */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar - Folders */}
        <div className="w-48 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 overflow-y-auto">
          <div className="p-2 space-y-0.5">
            {folders.map(folder => {
              const Icon = getFolderIcon(folder.name);
              const isActive = selectedFolder === folder.name;
              return (
                <button
                  key={folder.name}
                  onClick={() => { setSelectedFolder(folder.name); setSelectedMessage(null); }}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs transition-colors ${
                    isActive
                      ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 font-medium'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span className="flex-1 text-left truncate">{getFolderLabel(folder.name)}</span>
                  {folder.unread > 0 && (
                    <span className="text-xs font-bold text-primary-600 dark:text-primary-400">{folder.unread}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Message list */}
        <div className={`${selectedMessage ? 'w-80' : 'flex-1'} border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 flex flex-col overflow-hidden transition-all`}>
          {/* Search */}
          <div className="p-2 border-b border-gray-200 dark:border-gray-700">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
              <input type="text" placeholder="Search emails..." className="input pl-7 text-xs"
                value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto">
            {loadingMessages ? (
              <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-primary-500" /></div>
            ) : messages.length === 0 ? (
              <div className="text-center py-8 text-xs text-gray-400">No messages in {getFolderLabel(selectedFolder)}</div>
            ) : (
              messages.map(msg => (
                <div
                  key={msg.id}
                  onClick={() => handleSelectMessage(msg)}
                  className={`px-3 py-2 border-b border-gray-100 dark:border-gray-800 cursor-pointer transition-colors ${
                    selectedMessage?.id === msg.id ? 'bg-primary-50 dark:bg-primary-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
                  } ${!msg.is_read ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''}`}
                >
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1 mb-0.5">
                        {!msg.is_read && <div className="w-1.5 h-1.5 rounded-full bg-primary-500 flex-shrink-0" />}
                        <span className={`text-xs truncate ${!msg.is_read ? 'font-semibold text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'}`}>
                          {msg.from_address.split('<')[0].trim() || msg.from_address}
                        </span>
                      </div>
                      <p className={`text-xs truncate ${!msg.is_read ? 'font-medium text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400'}`}>
                        {msg.subject || '(No Subject)'}
                      </p>
                      {msg.snippet && <p className="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5">{msg.snippet}</p>}
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <span className="text-xs text-gray-400">{formatDate(msg.date)}</span>
                      <div className="flex items-center gap-1">
                        {msg.has_attachments && <Paperclip className="w-3 h-3 text-gray-400" />}
                        <button onClick={(e) => handleToggleRead(msg, e)} className="p-0.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded" title={msg.is_read ? 'Mark unread' : 'Mark read'}>
                          {msg.is_read ? <EyeOff className="w-3 h-3 text-gray-400" /> : <Eye className="w-3 h-3 text-gray-400" />}
                        </button>
                        <button onClick={(e) => handleDelete(msg, e)} className="p-0.5 hover:bg-red-100 dark:hover:bg-red-900/30 rounded">
                          <Trash2 className="w-3 h-3 text-gray-400" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          {totalMessages > 0 && (
            <div className="p-2 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-400 text-center">
              {totalMessages} messages
            </div>
          )}
        </div>

        {/* Reading pane */}
        {selectedMessage && (
          <div className="flex-1 bg-white dark:bg-gray-900 flex flex-col overflow-hidden">
            {loadingMessage ? (
              <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-primary-500" /></div>
            ) : (
              <>
                {/* Message header */}
                <div className="p-3 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-start justify-between mb-2">
                    <h2 className="text-sm font-semibold text-gray-900 dark:text-white pr-4">{selectedMessage.subject || '(No Subject)'}</h2>
                    <button onClick={() => setSelectedMessage(null)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded flex-shrink-0">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-gray-700 dark:text-gray-300">{selectedMessage.from_address}</p>
                      <p className="text-xs text-gray-400">
                        To: {selectedMessage.to_addresses.join(', ')}
                        {selectedMessage.cc_addresses.length > 0 && <><br />Cc: {selectedMessage.cc_addresses.join(', ')}</>}
                      </p>
                    </div>
                    <span className="text-xs text-gray-400">{new Date(selectedMessage.date).toLocaleString()}</span>
                  </div>
                  <div className="flex gap-1 mt-2">
                    <button onClick={() => openCompose('reply', selectedMessage)} className="btn btn-ghost text-xs">
                      <Reply className="w-3 h-3" /> Reply
                    </button>
                    <button onClick={() => openCompose('forward', selectedMessage)} className="btn btn-ghost text-xs">
                      <Forward className="w-3 h-3" /> Forward
                    </button>
                  </div>
                </div>
                {/* Message body */}
                <div className="flex-1 overflow-y-auto p-3">
                  {selectedMessage.html_body ? (
                    <div
                      className="text-sm text-gray-800 dark:text-gray-200 email-body"
                      dangerouslySetInnerHTML={{ __html: selectedMessage.html_body }}
                    />
                  ) : (
                    <pre className="text-xs text-gray-800 dark:text-gray-200 whitespace-pre-wrap font-sans">
                      {selectedMessage.plain_body || 'No content'}
                    </pre>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* No message selected */}
        {!selectedMessage && messages.length > 0 && (
          <div className="flex-1 bg-white dark:bg-gray-900 flex items-center justify-center">
            <div className="text-center">
              <Mail className="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
              <p className="text-xs text-gray-400">Select a message to read</p>
            </div>
          </div>
        )}
      </div>

      {/* Compose Modal */}
      {showCompose && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3">
          <div className="bg-white dark:bg-gray-900 rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto border border-light-border dark:border-dark-border">
            <div className="flex items-center justify-between p-3 border-b border-light-border dark:border-dark-border">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                {composeMode === 'reply' ? 'Reply' : composeMode === 'forward' ? 'Forward' : 'New Email'}
              </h2>
              <button onClick={() => setShowCompose(false)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-3 space-y-2">
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">To *</label>
                <input type="text" value={composeData.to}
                  onChange={(e) => setComposeData({ ...composeData, to: e.target.value })}
                  className="input text-xs" placeholder="recipient@example.com" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Cc</label>
                  <input type="text" value={composeData.cc}
                    onChange={(e) => setComposeData({ ...composeData, cc: e.target.value })}
                    className="input text-xs" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Bcc</label>
                  <input type="text" value={composeData.bcc}
                    onChange={(e) => setComposeData({ ...composeData, bcc: e.target.value })}
                    className="input text-xs" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Subject</label>
                <input type="text" value={composeData.subject}
                  onChange={(e) => setComposeData({ ...composeData, subject: e.target.value })}
                  className="input text-xs" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Message</label>
                <textarea
                  value={composeData.body}
                  onChange={(e) => setComposeData({ ...composeData, body: e.target.value })}
                  className="input text-xs" rows={10}
                  placeholder="Write your message..."
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setShowCompose(false)} className="btn btn-secondary text-xs">Cancel</button>
                <button onClick={handleSend} disabled={sending || !composeData.to.trim()} className="btn btn-primary text-xs">
                  {sending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  <Send className="w-3.5 h-3.5" /> Send
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {renderAccountModal()}
    </div>
  );
};

export default EmailPage;
